import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useLenis } from 'lenis/react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigation } from 'react-router'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { cn, getMaterialDesignImgSrc } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { PlainImage } from '../ui/image'

if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger)
}

export default function OtherProductsSection({
	products,
}: {
	products: {
		name: string
		slug: string
		designsImages: {
			id: string
		}[]
	}[]
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const productRefs = useRef<HTMLDivElement[]>([])
	const imageRefs = useRef<HTMLDivElement[]>([])
	const titleRefs = useRef<HTMLHeadingElement[]>([])
	// Initialize Lenis for smooth scroll
	const lenis = useLenis()
	const navigation = useNavigation()
	const { t } = useTranslation('products')
	const ref = useChangeBackgroundColor(extendedTheme.colors.brand.dusk.DEFAULT)

	useEffect(() => {
		if (navigation.state === 'loading') {
			ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
		}
	}, [navigation])

	useGSAP(() => {
		if (ScrollTrigger.getById('horizontal-scroll')) {
			ScrollTrigger.getById('horizontal-scroll')?.kill()
		}

		// GSAP Horizontal Scroll with ScrollTrigger
		const container = containerRef.current

		function getScrollAmount() {
			if (!container) return 0
			return -(container.offsetWidth - window.innerWidth)
		}

		// Set initial styles for all products
		productRefs.current.forEach((productEl, idx) => {
			if (!productEl) return

			const imageWrapper = imageRefs.current[idx]
			const titleEl = titleRefs.current[idx]

			if (imageWrapper && titleEl) {
				gsap.set(imageWrapper, {
					y: 40,
					filter: 'grayscale(100%) brightness(0.5)',
					opacity: 0.7,
				})

				gsap.set(titleEl, {
					opacity: 0,
				})
			}
		})

		gsap.to(container, {
			x: getScrollAmount,
			ease: 'none',
			scrollTrigger: {
				id: 'horizontal-scroll',
				trigger: '#section-to-pin',
				start: 'top top',
				end: () => `+=${Math.abs(getScrollAmount())}`,
				scrub: 1,
				pin: true,
				invalidateOnRefresh: true,
				onUpdate: (self) => {
					// Calculate which product should be active based on scroll progress
					const progress = self.progress
					const totalProducts = productRefs.current.length

					// Calculate the new active index more smoothly
					const exactIndex = progress * (totalProducts - 0.5)
					const newActiveIndex = Math.round(exactIndex)

					// Deactivate the previous active item
					for (let i = 0; i < totalProducts; i++) {
						if (i === newActiveIndex) continue
						const prevImageWrapper = imageRefs.current[i]
						const prevTitleEl = titleRefs.current[i]

						if (prevImageWrapper && prevTitleEl) {
							gsap.to(prevImageWrapper, {
								y: 0,
								filter: 'grayscale(100%) brightness(0.7)',
								delay: 0.1,
								duration: 0.2,
								ease: 'power2.out',
							})

							gsap.to(prevTitleEl, {
								opacity: 0,
								duration: 0.05,
								ease: 'none',
							})
						}
					}

					// Activate the new item
					const imageWrapper = imageRefs.current[newActiveIndex]
					const titleEl = titleRefs.current[newActiveIndex]

					const titleHeight = titleEl?.clientHeight

					if (imageWrapper && titleEl) {
						gsap.to(imageWrapper, {
							y: -(titleHeight ? titleHeight + 20 : 100), // Move image up to reveal title
							filter: 'grayscale(0%) brightness(1)',
							opacity: 1,
							duration: 0.1,
							ease: 'power2.out',
						})

						gsap.to(titleEl, {
							opacity: 1,
							duration: 0.1,
							ease: 'power2.out',
						})
					}
				},
			},
		})

		// Clean up GSAP and ScrollTrigger on unmount
		return () => {
			ScrollTrigger.getById('horizontal-scroll')?.kill()
		}
	}, [lenis])

	if (products.length === 0) return null

	return (
		<div
			ref={ref}
			id="section-to-pin"
			className={cn('relative', 'text-brand-dusk-foreground')}
		>
			<div
				id="section-pin"
				className="sticky top-0 h-screen max-w-[100vw] lg:max-w-[calc(100vw-100px)]"
			>
				<div className="flex h-screen flex-row overflow-hidden">
					<div
						id="content-wrapper"
						className="md:text-auto absolute left-1/2 top-md z-30 min-w-fit -translate-x-1/2 p-sm text-start md:relative md:left-auto md:top-auto md:translate-x-0 2xl:px-xxxl 2xl:py-xxxl"
					>
						<h2 className="text-body-2xs lowercase tracking-[0.2em] sm:text-body-xs 2xl:text-body-sm">
							/ {t('public.product.others')} /
						</h2>
					</div>

					{/*<div*/}
					{/*	id="gradient-overlay-right"*/}
					{/*	className="absolute bottom-0 right-0 top-0 z-20 w-[5%] lg:w-[10%]"*/}
					{/*	style={{*/}
					{/*		background: `linear-gradient(to left,  ${gradientColor(1)}) 0%, ${gradientColor(0.3)} 50%, ${gradientColor(0.15)} 65%, ${gradientColor(0.075)} 75.5%, ${gradientColor(0.037)} 82.85%, ${gradientColor(0.019)} 88%, ${gradientColor(0)} 98%)`,*/}
					{/*	}}*/}
					{/*></div>*/}

					{/*<div*/}
					{/*	id="gradient-overlay-left"*/}
					{/*	className="absolute bottom-0 left-0 top-0 z-20 w-[5%] lg:w-[10%]"*/}
					{/*	style={{*/}
					{/*		background: `linear-gradient(to right,  ${gradientColor(1)}) 0%, ${gradientColor(0.3)} 50%, ${gradientColor(0.15)} 65%, ${gradientColor(0.075)} 75.5%, ${gradientColor(0.037)} 82.85%, ${gradientColor(0.019)} 88%, ${gradientColor(0)} 98%)`,*/}
					{/*	}}*/}
					{/*></div>*/}

					<div
						ref={containerRef}
						className="z-10 mb-[10%] mt-auto flex gap-sm overflow-x-visible lg:mb-[5%]"
					>
						<div
							id="intitial-spacing"
							className="block w-[40vw] md:w-[20vw] lg:w-[35vw]"
						/>

						{products.map((product, i) => (
							<div
								ref={(el) => (productRefs.current[i] = el as HTMLDivElement)}
								className="relative isolate flex h-[50vh] min-w-[60vw] max-w-[450px] flex-col justify-end gap-sm md:min-w-[300px] md:max-w-[12vw] lg:h-[60vh] min-[2000px]:max-w-none"
								key={`${product.name}-${i}`}
							>
								<Link
									className="absolute inset-0 z-30"
									to={routes.public.product.route({
										slug: product.slug,
									})}
								/>
								<div
									ref={(el) => (imageRefs.current[i] = el as HTMLDivElement)}
									className="relative !z-20 transition-all duration-500 ease-out will-change-transform"
								>
									<PlainImage
										className="aspect-auto"
										alt={product.name}
										src={getMaterialDesignImgSrc(product.designsImages[0]!.id)}
									/>
								</div>

								<h3
									ref={(el) =>
										(titleRefs.current[i] = el as HTMLHeadingElement)
									}
									className="absolute bottom-0 left-0 z-10 mt-4 text-wrap break-words font-serif text-[3em] font-light uppercase leading-[0.9] opacity-0 transition-all duration-500"
								>
									{product.name}
								</h3>
							</div>
						))}

						<div
							id="final-spacing"
							className="block w-[50vw] md:w-[100vw] lg:w-[80vw]"
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
